import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import UpcomingMaintenanceItemSummary, {
  hasUpcomingSource,
  getSourceLabel,
  getIntimationSourceDisplay
} from './UpcomingMaintenanceItemSummary';

/**
 * Link an existing intimation ↔ upcoming maintenance row (retroactive).
 * mode: fromIntimation — pick upcoming item; fromUpcoming — pick intimation.
 */
const LinkIntimationSourceDialog = ({
  open,
  onClose,
  mode,
  intimation,
  upcomingItem,
  intimations = [],
  upcomingMaintenance = [],
  displayTimezone,
  onSuccess
}) => {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingUpcoming, setFetchingUpcoming] = useState(false);
  const [upcomingItems, setUpcomingItems] = useState([]);
  const [selectedUpcoming, setSelectedUpcoming] = useState(null);
  const [selectedIntimation, setSelectedIntimation] = useState(null);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const aircraftId = mode === 'fromIntimation'
    ? (intimation?.aircraftId?._id || intimation?.aircraftId)
    : (upcomingItem?.aircraftId?._id || upcomingItem?.aircraftId);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedUpcoming(null);
      setSelectedIntimation(null);
      setError('');
      setUpcomingItems([]);
      return;
    }

    if (mode === 'fromUpcoming' && upcomingItem) {
      setSelectedUpcoming(upcomingItem);
    }
    if (mode === 'fromIntimation' && intimation) {
      setSelectedIntimation(intimation);
    }
  }, [open, mode, intimation, upcomingItem]);

  useEffect(() => {
    if (!open || mode !== 'fromIntimation' || !aircraftId) return undefined;

    let cancelled = false;
    setFetchingUpcoming(true);

    const cached = (upcomingMaintenance || []).filter((item) => {
      const itemAc = item.aircraftId?._id || item.aircraftId;
      return itemAc && String(itemAc) === String(aircraftId);
    });

    if (cached.length > 0) {
      setUpcomingItems(cached);
      setFetchingUpcoming(false);
      return undefined;
    }

    axios
      .get('/api/maintenance/upcoming', {
        params: {
          pastDays: 90,
          useDays: '1',
          days: 365,
          useHours: '1',
          hours: 500,
          useCycles: '1',
          cycles: 5000,
          useLandings: '1',
          landings: 2500,
          aircraftId: String(aircraftId)
        }
      })
      .then((res) => {
        if (cancelled) return;
        setUpcomingItems(res.data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setUpcomingItems([]);
      })
      .finally(() => {
        if (!cancelled) setFetchingUpcoming(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, mode, aircraftId, upcomingMaintenance]);

  const eligibleIntimations = useMemo(() => {
    if (mode !== 'fromUpcoming' || !aircraftId) return [];
    return (intimations || []).filter((row) => {
      if (row.status === 'ARCHIVED') return false;
      if (hasUpcomingSource(row)) return false;
      const rowAc = row.aircraftId?._id || row.aircraftId;
      return rowAc && String(rowAc) === String(aircraftId);
    });
  }, [mode, intimations, aircraftId]);

  const filteredUpcoming = useMemo(() => {
    const q = search.trim().toLowerCase();
    const withoutIntimation = (upcomingItems || []).filter((item) => !item.intimationId);
    if (!q) return withoutIntimation;
    return withoutIntimation.filter((item) =>
      (item.title || '').toLowerCase().includes(q) ||
      (item.description || '').toLowerCase().includes(q) ||
      getSourceLabel(item.source).toLowerCase().includes(q) ||
      (item.componentName || '').toLowerCase().includes(q)
    );
  }, [upcomingItems, search]);

  const filteredIntimations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return eligibleIntimations;
    return eligibleIntimations.filter((row) =>
      (row.title || '').toLowerCase().includes(q) ||
      (row.includedItems || '').toLowerCase().includes(q) ||
      (row.sourcePlainText || '').toLowerCase().includes(q) ||
      (row.status || '').toLowerCase().includes(q)
    );
  }, [eligibleIntimations, search]);

  const handleSubmit = async () => {
    const targetIntimation = mode === 'fromIntimation' ? intimation : selectedIntimation;
    const targetUpcoming = mode === 'fromIntimation' ? selectedUpcoming : upcomingItem;

    if (!targetIntimation?._id) {
      setError('Select an intimation');
      return;
    }
    if (!targetUpcoming?.source || targetUpcoming.sourceId == null) {
      setError('Select an upcoming maintenance item');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = {
        sourceType: targetUpcoming.source,
        sourceId: String(targetUpcoming.sourceId),
        aircraftId: String(targetUpcoming.aircraftId?._id || targetUpcoming.aircraftId || aircraftId || '')
      };
      if (targetUpcoming.tsrIndex != null) {
        payload.sourceMetadata = { tsrIndex: targetUpcoming.tsrIndex };
      } else if (targetUpcoming.scheduleIndex != null) {
        payload.sourceMetadata = { scheduleIndex: targetUpcoming.scheduleIndex };
      }

      const res = await axios.post(`/api/intimations/${targetIntimation._id}/link-source`, payload);
      showToast(res.data?.message || 'Intimation linked to upcoming maintenance', 'success');
      onSuccess?.(res.data?.intimation);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to link intimation to source';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const title = mode === 'fromIntimation'
    ? 'Link intimation to Upcoming Maintenance'
    : 'Link existing intimation to this item';

  const canSubmit = mode === 'fromIntimation'
    ? !!selectedUpcoming
    : !!selectedIntimation;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Links an existing intimation to an upcoming maintenance item without creating a new work order or changing intimation status.
          Use this when planning created an intimation first and the AMP/AD/SB or other source appeared later.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {mode === 'fromIntimation' && intimation && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Intimation</Typography>
            <Typography variant="body2"><strong>{intimation.title}</strong></Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {intimation.aircraftId?.registration || '—'} · {intimation.status}
              {intimation.workOrderNumber || intimation.linkedWorkOrderId?.workOrderNumber
                ? ` · WO ${intimation.workOrderNumber || intimation.linkedWorkOrderId?.workOrderNumber}`
                : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Current source: {getIntimationSourceDisplay(intimation)}
            </Typography>
          </Box>
        )}

        {mode === 'fromUpcoming' && upcomingItem && (
          <Box sx={{ mb: 2 }}>
            <UpcomingMaintenanceItemSummary
              item={upcomingItem}
              displayTimezone={displayTimezone}
              title="Upcoming maintenance item"
            />
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder={mode === 'fromIntimation' ? 'Search upcoming items…' : 'Search intimations…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />

        {mode === 'fromIntimation' && (
          <>
            {fetchingUpcoming ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : filteredUpcoming.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No linkable upcoming items for this aircraft (items that already have an intimation are hidden).
              </Typography>
            ) : (
              <List dense sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {filteredUpcoming.map((item, idx) => {
                  const key = `${item.source}-${item.sourceId}-${item.tsrIndex ?? ''}-${idx}`;
                  const selected = selectedUpcoming === item;
                  return (
                    <ListItemButton
                      key={key}
                      selected={selected}
                      onClick={() => setSelectedUpcoming(item)}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight={500}>{item.title}</Typography>
                            <Chip label={getSourceLabel(item.source)} size="small" variant="outlined" />
                          </Box>
                        }
                        secondary={item.description && item.description !== item.title ? item.description : undefined}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
            {selectedUpcoming && (
              <Box sx={{ mt: 2 }}>
                <UpcomingMaintenanceItemSummary
                  item={selectedUpcoming}
                  displayTimezone={displayTimezone}
                  title="Selected upcoming item"
                />
              </Box>
            )}
          </>
        )}

        {mode === 'fromUpcoming' && (
          <>
            {filteredIntimations.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No unlinked intimations for this aircraft. Only intimations without an existing upcoming source can be linked.
              </Typography>
            ) : (
              <List dense sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {filteredIntimations.map((row) => (
                  <ListItemButton
                    key={row._id}
                    selected={selectedIntimation?._id === row._id}
                    onClick={() => setSelectedIntimation(row)}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={500}>{row.title}</Typography>
                          <Chip label={row.status} size="small" />
                          {(row.workOrderNumber || row.linkedWorkOrderId?.workOrderNumber) && (
                            <Chip
                              label={`WO ${row.workOrderNumber || row.linkedWorkOrderId?.workOrderNumber}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={row.sourcePlainText || row.includedItems?.slice(0, 120)}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !canSubmit}>
          {loading ? 'Linking…' : 'Link to source'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkIntimationSourceDialog;
